import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { useClickAway } from 'react-use'

import { FileCategory } from '@makyoapp/makyo-sdk/models/Files'
import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter } from '@makyoapp/makyo-common'
import { AttachFile16, ChevronRight, Edit16, MoreVert24, Search16 } from '@makyoapp/makyo-icon/icon'
import * as Mime from '@makyoapp/makyo-icon/mimetype'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, DD, DDItem, DropdownV2, SimpleInput, SimpleInputProps, TextArea, useTheme } from '@makyoapp/makyo-ui'

import { cn } from '@/lib/cn'
import constate from '@/lib/constate'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useActionFormContextV2 } from '@/context/ActionFormContextV2'
import { useDbDataV2 } from '@/hooks/useDbDataV2'
import { ActionDataType, ActionInputdata } from '@/shared/action.types'
import { SHOW_SEARCH_LIMIT } from '@/shared/constants'
import { appIconKeys } from '@/shared/icons'
import { getTableColumnsWithRowId, isEmpty } from '@/shared/utils'
import { Help } from '@/components/Help'

import { FileManager } from '../FileManager/FileManager'
import { getMimeIcon } from '../FileManager/MyFilesPanel'
import { RenderColumn } from '../RenderColumn'
import { VariableColumnDd } from '../VariableSelectV2/VariableColumnDd'
import { VariableTable } from '../VariableSelectV2/VariableTable'
import { RenderSelectedColumn } from './ActionInputRenderers'

const [ActionInputContextProvider, useActionInputContext] = constate('ActionInputContext', () => {
  const [activeDropdown, setActiveDropdown] = useState('')

  useEffect(() => {
    setActiveDropdown('')
  }, [])

  return {
    activeDropdown,
    setActiveDropdown,
  }
})

const columnTypesDefault: ColumnTypes[] = [
  ColumnTypes.Text,
  ColumnTypes.Number,
  ColumnTypes.Email,
  ColumnTypes.Phone,
  ColumnTypes.Emoji,
  ColumnTypes.Icon,
  ColumnTypes.Color,
  ColumnTypes.Address,
  ColumnTypes.Number,
  ColumnTypes.Rating,
  ColumnTypes.Boolean,
  ColumnTypes.DateTime,
  ColumnTypes.MultiSelect,
  ColumnTypes.Coordinate,
  ColumnTypes.Function,
  ColumnTypes.Image,
  ColumnTypes.Array,
  ColumnTypes.RichText,
  ColumnTypes.Template,
  ColumnTypes.Math,
  ColumnTypes.SingleCell,
]

const columnTypesFile: ColumnTypes[] = [ColumnTypes.Image, ColumnTypes.URL, ColumnTypes.Attachment]

type ColumnDataType = 'profile' | 'single-cell' | 'session-id' | 'unique-id'

const defaultColumnDataTypes: ColumnDataType[] = ['profile', 'single-cell', 'session-id', 'unique-id']

type RenderCustomInputProps = {
  value?: any
  onChange?(data: ActionInputdata): void
  onToggleCustomDropdown?(): void
  onToggleFileDropdown?(): void
  onClear?(): void
  ref?: any
  triggerRef?: any
}

export type ActionInputPropsV2 = {
  id?: string
  className?: string
  placeholder?: string
  inputType?: 'text' | 'textarea' | 'number'
  type?: 'text' | 'file'
  fileTypes?: FileCategory[]
  resetValue?: any
  value?: ActionInputdata
  onChange(data: ActionInputdata): void
  onClear?(): any
  error?: React.ReactNode
  columnTypes?: ColumnTypes[]
  inputProps?: SimpleInputProps
  columnDataTypes?: ColumnDataType[]
  formatDisplay?(val: any): any
  renderCustomInput?(props: RenderCustomInputProps, ref?: any): React.ReactNode
  renderCustomDropdown?(props: RenderCustomInputProps, ref?: any): React.ReactNode
  icon?: React.ReactNode
  globalTableId?: string
}

/**
 * Action Input Component
 *
 * implementation:
 * - makyo action forms
 *
 * @note this is a controlled component
 */
export const ActionInputV2 = ({
  className,
  placeholder,
  inputType = 'text',
  type = 'text',
  fileTypes,
  resetValue = '',
  value,
  onChange,
  error,
  columnTypes,
  columnDataTypes = defaultColumnDataTypes,
  inputProps,
  formatDisplay,
  renderCustomInput,
  renderCustomDropdown,
  icon,
  globalTableId,
}: ActionInputPropsV2) => {
  const { actionTriggerSource, actionFormShowClearValue } = useActionFormContextV2()

  // current input data value
  const inputData: ActionInputdata = {
    value: value?.value ?? (typeof resetValue !== 'undefined' ? resetValue : ''),
    dataType: value?.dataType,
    rowIndex: value?.rowIndex,
    tableId: value?.tableId,
  }

  // Tables & Columns State
  const { tables } = useDbClientContext()

  const { activeComponent, activeLayerContainer } = useActiveLayerContext()
  const isDetailPage = !!activeLayerContainer?.tableId

  const showDataSource = isDetailPage || ['menu-item', 'list'].includes(actionTriggerSource || '')
  const showCommentDataType = ['comment', 'comment'].includes(actionTriggerSource || '')
  const showNewsletterDataType = ['newsletter', 'newsletter'].includes(actionTriggerSource || '')

  const [isEditing, setIsEditing] = useState(false)

  // get the tableId (datasource) from 'list' component
  const isList = activeComponent?.groupId === 'list'
  const elSettings = !activeComponent ? undefined : activeLayerContainer?.elementSettings[activeComponent.id]
  const elSettingsDataSourceKey = !elSettings
    ? undefined
    : Object.keys(elSettings).find((key) => key.includes('data-source'))
  const elSettingsTableId =
    elSettings && elSettingsDataSourceKey ? (elSettings[elSettingsDataSourceKey] as string) : undefined

  // Check if current page has tableId (in detail page)
  const currentTableId = useMemo(() => {
    // return global tableId if any
    if (!!globalTableId) return globalTableId

    // return tableId from input form first
    if (inputData?.tableId) return inputData?.tableId

    // return tableId (datasource) from list component if exist
    if (isList && !!elSettingsTableId) return elSettingsTableId

    // return tableId from detail page
    if (isDetailPage) return activeLayerContainer?.tableId

    return ''
  }, [activeLayerContainer?.tableId, elSettingsTableId, globalTableId, inputData?.tableId, isDetailPage, isList])

  // get filtered column types
  const filteredColTypes = useMemo(() => {
    if (columnTypes) {
      const additionalTypes = [ColumnTypes.Function, ColumnTypes.SingleCell, ColumnTypes.Template]

      // if (columnTypes.some((c) => [ColumnTypes.Text, ColumnTypes.RichText, ColumnTypes.URL].includes(c))) {
      //   additionalTypes.push(ColumnTypes.Template)
      // }
      if (columnTypes.some((c) => [ColumnTypes.Number, ColumnTypes.Math].includes(c))) {
        additionalTypes.push(ColumnTypes.Math)
      }
      return columnTypes.concat(additionalTypes)
    }

    if (type === 'file') {
      return columnTypesFile
    }

    return columnTypesDefault
  }, [columnTypes, type])

  const currentRowColOptions = useMemo(() => {
    let tblObj = tables.find((tb) => tb.id === currentTableId)
    let cols = tblObj?.columns.filter((c) => filteredColTypes.includes(c.type)) ?? []
    return [{ id: '_id', name: 'Row ID', type: ColumnTypes.RowID } as ColumnAdapter, ...cols]
  }, [currentTableId, filteredColTypes, tables])

  const showColumnOptions = !!currentTableId && !isEmpty(currentRowColOptions)

  const profileColOptions = useMemo(() => {
    const tblObj = tables.find((tb) => tb.id === window.appUserTableID)
    const cols = tblObj?.columns.filter((c) => filteredColTypes.includes(c.type)) ?? []
    return [{ id: '_id', name: 'Row ID', type: ColumnTypes.RowID } as ColumnAdapter, ...cols]
  }, [filteredColTypes, tables])

  const checkColumnDataType = useCallback(
    (value?: string) => {
      if (!value || typeof value !== 'string') return

      let profileDataCols = tables.find((tb) => tb.id === window.appUserTableID)?.columns
      let foundProfileDataCol = profileDataCols?.find((c) => c.id === value)
      if (foundProfileDataCol) {
        return {
          dataType: ActionDataType.ProfileData,
          selectedColumn: foundProfileDataCol,
        }
      }

      // TODO -> parse specific action type
      let commonColumns = getTableColumnsWithRowId(tables.find((tb) => tb.id === currentTableId)?.columns ?? [])
      let foundCommonColumn = commonColumns?.find((c) => c.id === value)
      if (foundCommonColumn) {
        return {
          dataType: ActionDataType.Column,
          selectedColumn: foundCommonColumn,
        }
      }
    },
    [currentTableId, tables]
  )
  const foundColumn = checkColumnDataType(inputData?.value)
  const hasColumn = !!inputData.dataType || !!foundColumn

  // Derived State
  const isFile = type === 'file'

  // Local State
  const [showDropdown, setShowDropdown] = useState(false)
  const [showFileDropdown, setShowFileDropdown] = useState(false)
  const [showCustomDropdown, setShowCustomDropdown] = useState(false)

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)
  const dropdownRef = useRef(null)

  // Logic Handlers
  const handleChange = (data: ActionInputdata) => {
    setIsEditing(true)

    let payload: ActionInputdata = {
      value: data.value,
      rowIndex: data.rowIndex,
      tableId: data.tableId,
      dataType: data.dataType,
    }

    let foundCol = checkColumnDataType(payload.value)
    let noColReference = !payload.dataType || !payload.tableId
    let isColumDataType = noColReference && foundCol?.dataType === ActionDataType.Column

    // Check if current value is column type
    if (isColumDataType) {
      payload.tableId = currentTableId
      payload.dataType = ActionDataType.Column
    }

    // reset to `resetValue` if provided
    if (typeof resetValue !== 'undefined' && data.value === resetValue) {
      payload.value = resetValue
    }

    onChange(payload)
  }

  const handleClearInput = (e?: any) => {
    if (e) {
      e.stopPropagation()
    }

    handleChange({ value: resetValue, tableId: undefined, rowIndex: undefined, dataType: undefined })

    if (inputRef.current) {
      inputRef.current.innerText = ''
    }
  }

  const handleSetCustomInput = () => {
    if (!!foundColumn?.selectedColumn) {
      handleClearInput()
    }
    setShowDropdown(false)
    inputRef.current?.focus()
  }

  // Render Handlers
  const renderCustomIcon = () => {
    if (isFile && !!inputData?.value) {
      const mime = getMimeIcon(inputData.value)
      const Icon = Mime[mime]

      if (!!Icon) {
        return (
          <div className='mk-input-file-preview'>
            <Icon />
          </div>
        )
      }
    }
    if (!!icon) {
      return icon
    }
    return null
  }

  // const getInputType = () => {
  //   if (!!formatDisplay && !isEditing) {
  //     let result = formatDisplay(inputData?.value)
  //     return typeof result === 'string' ? 'text' : inputType
  //   }
  //   return inputType
  // }

  // change input display based on the formatter
  useEffect(() => {
    if (inputType === 'number' && inputRef.current) {
      if (!!formatDisplay && !isEditing) {
        let currentDisplay = formatDisplay(inputData?.value) ?? ''
        inputRef.current.value = currentDisplay
      }
    }
  }, [formatDisplay, isEditing, inputData?.value, inputType])

  useClickAway(dropdownRef, (e) => {
    const tgt = e?.target as HTMLElement
    if (tgt?.closest('.sdd-menu-subc')) {
      return
    }
    setShowDropdown(false)
  })

  const handleToggleFileDD = () => {
    setShowFileDropdown((prev) => !prev)
  }

  const handleToggleCustomDD = () => {
    setShowCustomDropdown((prev) => !prev)
  }

  const isCustomInput = !!renderCustomInput

  return (
    <ActionInputContextProvider>
      <div className={cn('w-full relative h-fit select-none', className)}>
        {hasColumn ? (
          <RenderSelectedColumn
            tables={tables}
            dataType={inputData.dataType!}
            selectedColumn={foundColumn?.selectedColumn}
            value={inputData?.value}
            onClear={handleClearInput}
          />
        ) : renderCustomInput ? (
          renderCustomInput(
            {
              value: inputData?.value,
              onToggleCustomDropdown: handleToggleCustomDD,
              onToggleFileDropdown: handleToggleFileDD,
              onClear: handleClearInput,
            },
            inputRef
          )
        ) : inputType === 'textarea' ? (
          <TextArea
            ref={inputRef as any}
            className={cn('w-full! relative pr-8!')}
            placeholder={placeholder ?? 'Enter Value'}
            value={inputData?.value}
            onChange={(e) => {
              if (isFile) return
              handleChange({ value: e.currentTarget.value })
            }}
          />
        ) : inputType === 'number' && isEditing ? (
          <div className='mk-bg-hover px-2 pr-8 rounded-sm flex items-center w-full relative'>
            {icon && <div className='pl-2'>{icon}</div>}
            <NumericFormat
              autoFocus
              className='bg-transparent h-9 py-2 flex-1 px-2'
              allowNegative={true}
              thousandSeparator={','}
              decimalSeparator={'.'}
              value={inputData.value ?? ''}
              onValueChange={(val) => {
                handleChange({ value: val.floatValue })
              }}
              onBlur={() => {
                setIsEditing(false)
              }}
            />
          </div>
        ) : (
          <SimpleInput
            ref={inputRef as any}
            className={cn('relative pr-8!', isFile && '[&_input]:pointer-events-none [&_input]:cursor-pointer')}
            placeholder={placeholder ?? (isFile ? 'Choose File' : 'Enter Value')}
            value={inputData?.value ?? ''}
            readOnly={isFile}
            onChange={(e) => {
              if (isFile) return
              handleChange({ value: e.currentTarget.value })
            }}
            onClick={() => {
              if (isFile) {
                setShowFileDropdown((prev) => !prev)
              }
            }}
            onFocus={(e) => {
              if (!!formatDisplay) setIsEditing(true)
              setShowDropdown(false)
            }}
            onBlur={() => {
              if (!!formatDisplay) setIsEditing(false)
            }}
            showClear={!!inputData?.value}
            onClear={handleClearInput}
            before={renderCustomIcon()}
            {...inputProps}
          />
        )}

        {error && <div className='mk-input-error'>{error}</div>}

        {/* New Dropdown */}
        <DropdownV2 open={showDropdown} onOpenChange={setShowDropdown}>
          <DropdownV2.Trigger asChild>
            <div
              role='button'
              className='absolute right-[2px] top-[6px] text-light'
              onClick={() => {
                setShowDropdown((prev) => !prev)
              }}
            >
              <MoreVert24 />
            </div>
          </DropdownV2.Trigger>

          <DropdownV2.Content className='z-302 w-[210px] text-m-m' sideOffset={10} align='end'>
            <div className='relative'>
              {isDetailPage && (
                <DropdownWrapper
                  id='column'
                  label={<Trans>{`This Screen's Data`}</Trans>}
                  icon={<MaterialIcon icon={appIconKeys.tableColumn} size={16} />}
                >
                  {({ onClose }) => (
                    <ColumnDD
                      colOptions={currentRowColOptions}
                      value={inputData}
                      onChange={handleChange}
                      dataType={ActionDataType.CurrentRow}
                      onClose={() => {
                        setShowDropdown(false)
                        onClose()
                      }}
                    />
                  )}
                </DropdownWrapper>
              )}

              {isCustomInput || type === 'file' ? null : (
                <MenuItem icon={<Edit16 />} onClick={handleSetCustomInput}>
                  Enter Custom
                </MenuItem>
              )}

              {type === 'file' && (
                <MenuItem icon={<AttachFile16 />} onClick={() => setShowFileDropdown(true)}>
                  Choose File
                </MenuItem>
              )}

              {actionFormShowClearValue && (
                <MenuItem
                  icon={<MaterialIcon icon='change_circle' size={16} />}
                  onClick={() => {
                    handleChange({
                      dataType: ActionDataType.ClearValue,
                    })
                    setShowDropdown(false)
                  }}
                >
                  Clear Value
                </MenuItem>
              )}

              {showColumnOptions && !isDetailPage && (
                <DropdownWrapper
                  id='column'
                  label="This item's data"
                  icon={<MaterialIcon icon={appIconKeys.tableColumn} size={16} />}
                >
                  {({ onClose }) => (
                    <ColumnDD
                      colOptions={currentRowColOptions}
                      value={inputData}
                      onChange={handleChange}
                      onClose={() => {
                        setShowDropdown(false)
                        onClose()
                      }}
                    />
                  )}
                </DropdownWrapper>
              )}

              {showDataSource && (
                <DropdownWrapper
                  id='datasource'
                  label='Data Source'
                  icon={<MaterialIcon icon={appIconKeys.tableColumn} size={16} />}
                  isSelected={value?.dataType === ActionDataType.Column}
                >
                  {({ onClose }) => (
                    <DataSourceDD
                      colOptions={currentRowColOptions}
                      defaultValue={{
                        tableId: currentTableId,
                        dataType: ActionDataType.Column,
                      }}
                      onChange={handleChange}
                      onClose={() => {
                        setShowDropdown(false)
                        onClose()
                      }}
                      columnTypes={filteredColTypes}
                      globalTableId={globalTableId}
                    />
                  )}
                </DropdownWrapper>
              )}

              {columnDataTypes.includes('profile') && (
                <DropdownWrapper
                  id='profile'
                  label='Profile Data'
                  icon={<MaterialIcon icon='person' size={16} />}
                  isSelected={value?.dataType === ActionDataType.ProfileData}
                >
                  {({ onClose }) => (
                    <ProfileDataDD
                      colOptions={profileColOptions}
                      value={inputData}
                      onChange={handleChange}
                      onClose={() => {
                        setShowDropdown(false)
                        onClose()
                      }}
                    />
                  )}
                </DropdownWrapper>
              )}

              {columnDataTypes.includes('single-cell') && (
                <DropdownWrapper
                  id='single-cell'
                  label='Single Cell Value'
                  icon={<MaterialIcon icon={appIconKeys.columns['single-cell']} size={16} />}
                  isSelected={value?.dataType === ActionDataType.SingleCell}
                >
                  {({ onClose }) => (
                    <SingleCellDD
                      columnTypes={filteredColTypes}
                      value={inputData}
                      onChange={handleChange}
                      onClose={() => {
                        setShowDropdown(false)
                        onClose()
                      }}
                    />
                  )}
                </DropdownWrapper>
              )}

              {columnDataTypes.includes('session-id') && (
                <MenuItem
                  icon={<MaterialIcon icon={appIconKeys.tableSession} size={16} />}
                  onClick={() => {
                    handleChange({
                      dataType: ActionDataType.SessionID,
                    })
                    setShowDropdown(false)
                  }}
                >
                  Session ID
                </MenuItem>
              )}

              {columnDataTypes.includes('unique-id') && (
                <MenuItem
                  icon={<MaterialIcon icon={appIconKeys.uniqueId} size={16} />}
                  onClick={() => {
                    handleChange({
                      dataType: ActionDataType.UniqueID,
                    })
                    setShowDropdown(false)
                  }}
                >
                  <Trans>Unique Identifier</Trans>
                </MenuItem>
              )}

              {showCommentDataType && (
                <DropdownWrapper
                  id='comment'
                  label='Comment'
                  icon={<MaterialIcon icon='comment' size={16} />}
                  isSelected={value?.dataType === ActionDataType.Comment}
                >
                  {({ onClose }) => (
                    <CommentDD
                      value={inputData}
                      onChange={handleChange}
                      onClose={() => {
                        setShowDropdown(false)
                        onClose()
                      }}
                    />
                  )}
                </DropdownWrapper>
              )}

              {showNewsletterDataType && (
                <MenuItem
                  icon={<MaterialIcon icon={appIconKeys.newsletterEmail} size={16} />}
                  onClick={() => {
                    handleChange({
                      dataType: ActionDataType.NewsletterEmail,
                    })
                    setShowDropdown(false)
                  }}
                >
                  Newsletter Email
                </MenuItem>
              )}

              {type !== 'file' && columnTypes?.includes(ColumnTypes.DateTime) && (
                <MenuItem
                  icon={<MaterialIcon icon='event' size={16} />}
                  onClick={() => {
                    handleChange({
                      dataType: ActionDataType.DateTime,
                      value: new Date().toISOString(),
                    })
                    setShowDropdown(false)
                  }}
                >
                  Current Date & Time
                </MenuItem>
              )}
            </div>
          </DropdownV2.Content>
        </DropdownV2>

        {showCustomDropdown &&
          renderCustomDropdown?.(
            {
              value: inputData?.value,
              onChange: handleChange,
              onToggleCustomDropdown: handleToggleCustomDD,
              onToggleFileDropdown: handleToggleFileDD,
              ref: dropdownRef,
            },
            inputRef
          )}

        {showFileDropdown && (
          <FileDropdown
            onChange={(data) => {
              handleChange(data)
              setShowDropdown(false)
            }}
            onHide={() => {
              setShowFileDropdown(false)
            }}
            fileTypes={fileTypes}
          />
        )}
      </div>
    </ActionInputContextProvider>
  )
}

const MenuItem = ({ onClick, children, icon, className }: any) => {
  return (
    <div
      role='button'
      className={cn('py-2 px-4 flex items-center gap-2 hover:bg-zinc-100 dark:hover:bg-slate-700', className)}
      onClick={onClick}
    >
      {icon}
      {children}
    </div>
  )
}

interface DropdownWrapper {
  children: (props: { onClose(): void }) => React.ReactNode
  id: string
  label: React.ReactNode
  icon: React.ReactNode
  isSelected?: boolean
}

const DropdownWrapper = ({ children, id, icon, label, isSelected }: DropdownWrapper) => {
  const { activeDropdown, setActiveDropdown } = useActionInputContext()
  const { state: themeState } = useTheme()

  useEffect(() => {
    return () => {
      setActiveDropdown('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <DD
      className='w-full'
      show={activeDropdown === id}
      onToggle={(nextShow, meta) => {
        let event = meta.originalEvent
        const tgt = event?.target as HTMLElement
        if (tgt?.closest('.sdd-menu-subc')) {
          return
        }

        setActiveDropdown(nextShow ? id : '')
      }}
      menuWrapperClassName='action-menu-subc text-m-m rounded-sm'
      menuNoPad={true}
      menuAlwaysRender={false}
      placement={themeState.dir === 'rtl' ? 'left' : 'right'}
      toggleNoButton
      toggle={(active, { ...toggleProps }) => {
        return (
          <div
            role='button'
            className={cn(
              'py-2 px-4 flex items-center justify-between gap-2 hover:bg-zinc-100 dark:hover:bg-slate-700',
              isSelected && 'bg-zinc-100 hover:bg-zinc-200 dark:bg-slate-900'
            )}
            {...toggleProps}
          >
            <div className='flex items-center gap-2'>
              {icon}
              <span>{label}</span>
            </div>

            <ChevronRight />
          </div>
        )
      }}
    >
      <div className='relative'>
        {children({
          onClose: () => {
            setActiveDropdown('')
          },
        })}
      </div>
    </DD>
  )
}

interface ActionInnerDropdown {
  colOptions?: ColumnAdapter[]
  value?: ActionInputdata
  defaultValue?: ActionInputdata
  onChange: (value: ActionInputdata) => void
  onClose: () => void
  columnTypes?: ColumnTypes[]
  globalTableId?: string
  dataType?: ActionDataType
}

const ColumnDD = ({ colOptions, value, onChange, onClose, dataType }: ActionInnerDropdown) => {
  const { activeLayerContainer } = useActiveLayerContext()

  const [search, setSearch] = useState('')

  const filteredCols = useMemo(() => {
    if (search) {
      const searchValue = search.toLowerCase()
      return colOptions?.filter((col) => col.name.toLowerCase().includes(searchValue))
    }
    return colOptions
  }, [colOptions, search])

  const showSearch = colOptions && colOptions.length > SHOW_SEARCH_LIMIT

  return (
    <div>
      {showSearch && (
        <div className='flex border-b border-zinc-200 dark:border-slate-600'>
          <SimpleInput
            noBorder
            outlined
            className='mk-search-box pr-3!'
            placeholder=''
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value)
            }}
            showClear={!!search}
            onClear={() => {
              setSearch('')
            }}
            before={<Search16 className='mk-search-box-icon' />}
          />
        </div>
      )}

      <div className={cn('py-2 max-h-[260px] overflow-y-auto')}>
        {!filteredCols?.length ? (
          <div className='py-2 px-4 text-light'>No column found</div>
        ) : (
          filteredCols.map((col) => {
            const isSelected = col.id === value

            return (
              <DDItem
                key={col.id}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onChange({
                    value: col.id,
                    tableId: activeLayerContainer?.tableId,
                    dataType: dataType ?? ActionDataType.Column,
                  })
                  // setActiveInnerDropdown('')
                  onClose()
                }}
                className={cn(
                  'w-full',
                  isSelected ? 'bg-zinc-100 dark:bg-slate-700' : 'hover:bg-zinc-100 dark:hover:bg-slate-700'
                )}
              >
                <RenderColumn withStyle={false} col={col} />
              </DDItem>
            )
          })
        )}
      </div>
    </div>
  )
}

const DataSourceDD = ({
  value,
  defaultValue,
  onChange,
  onClose,
  columnTypes = Object.values(ColumnTypes),
  globalTableId,
}: ActionInnerDropdown) => {
  const { dbId } = useDbClientContext()

  const { activeLayerContainer } = useActiveLayerContext()

  const { tbl, setTbl, col, setCol, tblOptions, colOptions } = useDbDataV2(
    columnTypes,
    [],
    dbId,
    globalTableId || defaultValue?.tableId || activeLayerContainer?.tableId,
    value?.value
  )

  return (
    <div>
      <div className='flex-c flex-col py-2'>
        {!globalTableId && <VariableTable setTbl={setTbl} tbl={tbl} tblOptions={tblOptions} />}

        <VariableColumnDd setCol={setCol} col={col} colOptions={colOptions} />

        <div className='w-full px-2'>
          <Button
            className='w-full'
            disabled={!tbl || !col}
            onClick={() => {
              if (col) {
                onChange({
                  value: col,
                  tableId: tbl,
                  dataType: ActionDataType.Column,
                })
              }

              onClose()
            }}
          >
            <Trans>Select</Trans>
          </Button>
        </div>
      </div>
    </div>
  )
}

const ProfileDataDD = ({ colOptions, value, onChange, onClose }: ActionInnerDropdown) => {
  const [search, setSearch] = useState('')

  const filteredCols = useMemo(() => {
    if (search) {
      const searchValue = search.toLowerCase()
      return colOptions?.filter((col) => col.name.toLowerCase().includes(searchValue))
    }
    return colOptions
  }, [colOptions, search])

  const showSearch = colOptions && colOptions.length > SHOW_SEARCH_LIMIT

  return (
    <div>
      {showSearch && (
        <div className='flex border-b border-zinc-200 dark:border-slate-600'>
          <SimpleInput
            noBorder
            outlined
            className='mk-search-box pr-3!'
            placeholder=''
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value)
            }}
            showClear={!!search}
            onClear={() => {
              setSearch('')
            }}
            before={<Search16 className='mk-search-box-icon' />}
          />
        </div>
      )}

      <div className={cn('py-2 max-h-[260px] overflow-y-auto')}>
        {!filteredCols?.length ? (
          <div className='py-2 px-4 text-light'>No column found</div>
        ) : (
          filteredCols.map((col) => {
            const isSelected = col.id === value

            return (
              <DDItem
                key={col.id}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onChange({
                    value: col.id,
                    dataType: ActionDataType.ProfileData,
                  })
                  onClose()
                }}
                className={cn(
                  'w-full',
                  isSelected ? 'bg-zinc-100 dark:bg-slate-700' : 'hover:bg-zinc-100 dark:hover:bg-slate-700'
                )}
              >
                <RenderColumn withStyle={false} col={col} />
              </DDItem>
            )
          })
        )}
      </div>
    </div>
  )
}

// const TableSelect = ({
//   options,
//   value,
//   onChange,
//   error,
// }: {
//   options: TableAdapter[]
//   value?: TableAdapter
//   onChange(table: TableAdapter): void
//   error?: string | null
// }) => {
//   const ref = useRef(null)
//   const [show, setShow] = useState(false)

//   useClickAway(ref, () => {
//     setShow(false)
//   })

//   return (
//     <div ref={ref} className='flex-1'>
//       <SelectDropdown
//         show={show}
//         onToggle={setShow}
//         options={options}
//         value={value}
//         onChange={(tb, e) => {
//           e.preventDefault()
//           e.stopPropagation()

//           onChange(tb)
//         }}
//         withSearch={options.length > SHOW_SEARCH_LIMIT}
//         portalMenu={false}
//         menuFullWidth={false}
//         menuZIndex='z-302'
//         optionLabel={(tb) => `${tb?.name}`}
//         menuContainerClassName='mk-ag-sdd'
//         renderOption={(tb) => {
//           return (
//             <div className='flex-c'>
//               <RenderTableIcon table={tb} isPlaceholder={!tb || tb?.id === ''} />
//               <div>{tb?.id === '' ? <Trans>{tb?.name}</Trans> : tb?.name}</div>
//             </div>
//           )
//         }}
//       />
//       <div className='mk-input-error'>{error}</div>
//     </div>
//   )
// }

const SingleCellDD = ({ value, onChange, onClose, columnTypes = Object.values(ColumnTypes) }: ActionInnerDropdown) => {
  const [rowNum, setRowNum] = useState('1')

  const { dbId } = useDbClientContext()

  const { activeLayerContainer } = useActiveLayerContext()

  const { tbl, setTbl, col, setCol, tblOptions, colOptions } = useDbDataV2(
    columnTypes,
    [],
    dbId,
    value?.tableId || activeLayerContainer?.tableId,
    value?.value,
    true
  )

  return (
    <div>
      <div className='flex-c flex-col py-2'>
        <VariableTable setTbl={setTbl} tbl={tbl} tblOptions={tblOptions} />

        <VariableColumnDd setCol={setCol} col={col} colOptions={colOptions} />

        <div className='flex-c px-2 w-full'>
          <SimpleInput
            type='text'
            inputClassName='min-w-0'
            value={rowNum}
            placeholder={'Enter Row Number'}
            onChange={(e) => {
              const val = e.currentTarget.value
              if (val === '-' || val === '') {
                setRowNum(val)
              } else {
                const numVal = Number(val)
                if (!isNaN(numVal)) {
                  setRowNum(numVal.toString())
                }
              }
            }}
          />
          <Help>
            <Trans>
              <strong>Row Number:</strong> Use negative number to start from last row. eq: 1 equals first row, -1 equals
              last row.
            </Trans>
          </Help>
        </div>

        {/* {userSpecific !== undefined && setUserSpecific !== undefined && (
          <label className='px-2 py-2 w-full flex-c justify-between cursor-pointer'>
            <div>
              <Trans>Use user-specific data</Trans>
            </div>
            <Switch
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              disabled={forceUserSpecific}
              checked={userSpecific}
              onClick={() => (forceUserSpecific ? null : setUserSpecific(!userSpecific))}
            />
          </label>
        )} */}

        <div className='w-full px-2'>
          <Button
            className='w-full'
            disabled={!tbl || !col}
            onClick={() => {
              if (col) {
                onChange({
                  value: col,
                  tableId: tbl,
                  rowIndex: Number(rowNum) - 1,
                  dataType: ActionDataType.SingleCell,
                })
              }

              onClose()
            }}
          >
            <Trans>Select</Trans>
          </Button>
        </div>
      </div>
    </div>
  )
}

interface FileDropdownProps {
  onChange: (value: ActionInputdata) => void
  onHide: () => void
  fileTypes?: FileCategory[]
}

const FileDropdown = ({ onChange, onHide, fileTypes }: FileDropdownProps) => {
  const ref = useRef(null)

  useClickAway(ref, (e) => {
    let tgt = e.target as HTMLElement
    if (!(tgt.classList && tgt?.classList.contains('mk-input-div'))) {
      onHide()
    }
  })

  return (
    <div
      ref={ref}
      className='absolute top-[120%] right-0 left-0 z-10 mk-bg border border-zinc-200 dark:border-slate-600 shadow-sm'
    >
      <FileManager
        fileTypes={fileTypes}
        hideImagesByDefault={false}
        showHideImageToggle={false}
        modal={false}
        gridCols={3}
        maxHeight='20vh'
        forceUrlPanelHeight
        onChange={(ty, v) => {
          onChange({ value: v as string })
          onHide()
        }}
        onHide={onHide}
      />
    </div>
  )
}

const CommentDD = ({ value, onChange, onClose }: ActionInnerDropdown) => {
  const currentValue = value?.value

  return (
    <div>
      <div className={cn('py-2 max-h-[260px] overflow-y-auto')}>
        <DDItem
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onChange({
              value: 'comment',
              dataType: ActionDataType.Comment,
            })
            onClose()
          }}
          className={cn(
            'w-full flex items-center gap-2',
            currentValue === 'comment' ? 'bg-zinc-100 dark:bg-slate-700' : 'hover:bg-zinc-100 dark:hover:bg-slate-700'
          )}
        >
          <div className='flex items-center gap-2'>
            <MaterialIcon icon='comment' size={16} className='mt-1' />
            <Trans>Comment Input</Trans>
          </div>
        </DDItem>
        <DDItem
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onChange({
              value: 'parent-id',
              dataType: ActionDataType.Comment,
            })
            onClose()
          }}
          className={cn(
            'w-full',
            currentValue === 'parent-id' ? 'bg-zinc-100 dark:bg-slate-700' : 'hover:bg-zinc-100 dark:hover:bg-slate-700'
          )}
        >
          <div className='flex items-center gap-2'>
            <MaterialIcon icon='comment_bank' size={16} className='mt-1' />
            <Trans>Comment Parent ID</Trans>
          </div>
        </DDItem>
      </div>
    </div>
  )
}
